import * as React from 'react';
import {useSelector} from 'react-redux';
import {Prompt, Link as RouterLink} from 'react-router-dom';

import {getSavedFilters} from 'nutshell-core/saved-filters/saved-filters-selectors';

import {colors} from 'shells/colors/colors';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {PiechartRegularAiIcon} from 'shells/icon/svg/piechart-regular-ai-icon';
import {WarningIcon} from 'shells/icon/svg/warning-icon';
import {Link} from 'shells/link/link';
import {TwoLine} from 'shells/two-line/two-line';
import {Body} from 'shells/typography/body';

import type {GenerateReportStatus} from 'nutshell-graphql-types';

import type {ReportId} from '../report-types';

import {removeBannerInfo, removePrompt, getBannerInfo} from './helpers';

import styles from './ai-generated-banner.module.css';

type Props = {
    reportId: ReportId;
};

export function AiGeneratedBanner(props: Props) {
    const [, setShouldRemove] = React.useState(false);

    const savedFilters = useSelector(getSavedFilters);
    const savedFilter = savedFilters[props.reportId];
    // As of right now, a hidden report indicates that it was generated by AI
    if (!savedFilter || !savedFilter.isHidden) {
        return null;
    }

    const bannerInfo = getBannerInfo(savedFilter.id);
    if (!bannerInfo) {
        return null;
    }

    const isSuccess = bannerInfo.status === 'SUCCESS';

    return (
        <>
            <Prompt
                when={true}
                message={(location) => {
                    // We'll maintain the banner as long as the user is on the same page.
                    // We won't remove the banner if the user changes report parameters due to the fragility of this check
                    if (location.pathname === window.location.pathname) {
                        return true;
                    }

                    // We want to maintain the prompt if the user goes back to the overview page
                    // with the idea that they'll probably want to tweak the existing prompt.
                    if (!location.pathname.includes('/reports/overview')) {
                        removePrompt();
                    }

                    // Removes the banner info whenever a user navigates to another page
                    removeBannerInfo(savedFilter.id);

                    return true;
                }}
            />
            <FlashBanner
                type={isSuccess ? 'ai-info' : 'warning'}
                leftAlignContent={true}
                animate={false}
                onHidden={() => {
                    removeBannerInfo(savedFilter.id);
                    removePrompt();

                    // We need to set this to trigger a re-render so the banner gets removed
                    setShouldRemove(true);
                }}
            >
                <div className={styles.container}>
                    <TwoLine
                        icon={isSuccess ? PiechartRegularAiIcon : WarningIcon}
                        iconColor={isSuccess ? colors.aiPurpleMd : colors.yellow}
                        topRow={
                            <Body size='small' isBold={true} color='dark'>
                                {getBannerTitle(bannerInfo.status)}
                            </Body>
                        }
                        bottomRow={
                            <div className='text-wrap'>
                                <Body size='small' title={savedFilter.description}>
                                    {savedFilter.description}
                                </Body>
                            </div>
                        }
                    />
                    <Link
                        // @ts-expect-error
                        as={RouterLink}
                        size='small'
                        variant={isSuccess ? 'ai' : 'primary'}
                        to='/reports/overview'
                    >
                        Go back
                    </Link>
                </div>
            </FlashBanner>
        </>
    );
}

function getBannerTitle(status: GenerateReportStatus) {
    if (status === 'SUCCESS') {
        return 'Here is your report!';
    }

    return 'There was an error';
}
